import { MyProgressRedux } from 'common/modules/ReactShared/data/redux/member';
import { Member } from 'common/modules/ReactShared';
import { t, Translate } from 'i18n';
import { FC, useEffect } from 'react';
import { useTypedDispatch, useTypedSelector } from 'students/stores/_utils';
import LinkElem from 'students/views/shared/components/LinkElem';
import { customMediaQuery, onLightTheme } from 'students/views/shared/styled';
import styled, { css } from 'styled-components';
import { myProgressTokens } from '../../../myProgress/styles/tokens';

const FlameIcon: FC = () => (
  <SIcon viewBox="0 0 16 20" fill="none" aria-hidden="true">
    <path
      d="M8 0C8 3.5 3 5.2 3 10.6a5 5 0 0010 0c0-2.4-1.3-3.8-2.4-5.4-.2 1.3-.8 2.2-1.6 2.7C9.6 5.4 9.2 2.5 8 0z"
      fill="url(#teaser-flame-outer)"
    />
    <path
      d="M8 19.6c-1.6 0-2.9-1.2-2.9-2.8 0-1.5 1.2-2.4 1.8-3.8.8.9 2.8 2.1 2.8 3.8 0 1.6-1.1 2.8-1.7 2.8z"
      fill="url(#teaser-flame-inner)"
    />
    <defs>
      <linearGradient id="teaser-flame-outer" x1="8" y1="0" x2="8" y2="16">
        <stop stopColor="#FEAB2D" />
        <stop offset="1" stopColor="#F04A9C" />
      </linearGradient>
      <linearGradient id="teaser-flame-inner" x1="8" y1="12" x2="8" y2="20">
        <stop stopColor="#FED02D" />
        <stop offset="1" stopColor="#FEAB2D" />
      </linearGradient>
    </defs>
  </SIcon>
);

/** Stands in for the rewards label in the bar variant, which has no room for it. */
const RewardIcon: FC = () => (
  <SRewardIcon viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M8 1l2.1 4.3 4.7.7-3.4 3.3.8 4.7L8 11.8 3.8 14l.8-4.7L1.2 6l4.7-.7z"
      fill="#F04A9C"
    />
  </SRewardIcon>
);

/** 52164 -> "52.1k". Floored, so the teaser never rounds a total upwards. */
function formatCompact(value: number): string {
  if (value < 1000) return String(value);
  if (value < 1_000_000) return `${Math.floor(value / 100) / 10}k`;
  return `${Math.floor(value / 100_000) / 10}M`;
}

type tTone = 'orange' | 'lime' | 'pink';

export const TONE_COLOR: Record<tTone, string> = {
  orange: 'rgb(var(--ln-tone-orange))',
  lime: 'rgb(var(--ln-tone-lime))',
  pink: 'rgb(var(--ln-tone-pink))'
};

interface IProps {
  className?: string;
  /**
   * `card` is the block that sits in the home-page header. `bar` is the compact
   * pill the top navigation shows instead on small screens, where the header has
   * no room for a full-width card.
   */
  variant?: 'card' | 'bar';
}

/**
 * Home-page entry point to the progress page. Reads the same store slice the
 * progress page uses, so opening it from here finds the stats already loaded.
 */
const ProgressTeaser: FC<IProps> = ({ className, variant = 'card' }) => {
  const dispatch = useTypedDispatch();
  const { stats, loading, fetched } = useTypedSelector(
    Member.selectors.selectMyProgressStore
  );

  useEffect(() => {
    if (!fetched.stats && !loading.stats) {
      dispatch(MyProgressRedux.actions.getProgressStats());
    }
  }, [dispatch, fetched.stats, loading.stats]);

  // Supplementary block: stay invisible while loading or on failure rather than
  // reserving space in the greeting header for a skeleton that may never fill.
  if (!stats) return null;

  const streak = stats.streak?.current ?? 0;
  const isBar = variant === 'bar';

  return (
    <SCard
      className={className}
      to="/my-progress"
      $bar={isBar}
      aria-label={t('frontend.member.home_page.progress_teaser.aria_label', {
        streak,
        xp: stats.xp,
        rewards: stats.totalRewardsEarned
      })}
    >
      <SStats $bar={isBar}>
        <SStat>
          <SValueRow>
            <FlameIcon />
            <SValue $tone="orange" $bar={isBar}>
              {streak}
            </SValue>
          </SValueRow>
          {isBar ? null : (
            <SLabel>
              <Translate str="frontend.member.home_page.progress_teaser.streak_label" />
            </SLabel>
          )}
        </SStat>

        <SStat>
          <SValueRow>
            <SValue $tone="lime" $bar={isBar}>
              {formatCompact(stats.xp)}
            </SValue>
            {isBar ? (
              <SUnit aria-hidden="true">
                <Translate str="frontend.member.home_page.progress_teaser.xp_label" />
              </SUnit>
            ) : null}
          </SValueRow>
          {isBar ? null : (
            <SLabel>
              <Translate str="frontend.member.home_page.progress_teaser.xp_label" />
            </SLabel>
          )}
        </SStat>

        <SStat>
          <SValueRow>
            {isBar ? <RewardIcon /> : null}
            <SValue $tone="pink" $bar={isBar}>
              {stats.totalRewardsEarned}
            </SValue>
          </SValueRow>
          {isBar ? null : (
            <SLabel>
              <Translate
                str="frontend.member.home_page.progress_teaser.rewards_label"
                params={{ count: stats.totalRewardsEarned }}
              />
            </SLabel>
          )}
        </SStat>
      </SStats>
    </SCard>
  );
};

export default ProgressTeaser;

const cardStyles = css`
  display: block;
  min-width: 260px;
  padding: 16px 20px;
  border-radius: 16px;

  &:hover {
    background: var(--mp-surface-solid);
    transform: translateY(-2px);
  }

  ${customMediaQuery('mobile')} {
    width: 100%;
  }
`;

/**
 * Sits in the top navigation next to the chat, language and profile buttons, so
 * it takes only the width those leave behind: no fixed minimum, and both the
 * gaps and the type shrink with the viewport.
 */
const barStyles = css`
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  /* Keeps the row a 44px tap target without letting the pill outgrow the bar. */
  min-height: 44px;
  padding: 4px 10px;
  border-radius: 999px;

  &:hover {
    background: var(--mp-surface-solid);
  }
`;

const SCard = styled(LinkElem)<{ $bar: boolean }>`
  ${myProgressTokens};
  ${onLightTheme`
    --mp-surface: transparent;
    --mp-surface-solid: rgb(var(--ln-overlay) / 0.06);
    --mp-border: rgb(var(--ln-overlay) / 0.15);
    --mp-text-muted: rgb(var(--ln-content) / 0.7);
  `}

  background: var(--mp-surface);
  border: 1px solid var(--mp-border);
  text-decoration: none;
  transition: transform 0.2s ease, background 0.2s ease;

  &:focus-visible {
    outline: 2px solid var(--mp-focus-ring);
    outline-offset: 2px;
  }

  ${({ $bar }) => ($bar ? barStyles : cardStyles)}
`;

const SStats = styled.div<{ $bar: boolean }>`
  display: flex;
  align-items: ${({ $bar }) => ($bar ? 'center' : 'flex-start')};
  min-width: 0;
  gap: ${({ $bar }) => ($bar ? 'clamp(8px, 3vw, 16px)' : '24px')};
`;

const SStat = styled.div`
  min-width: 0;
`;

const SValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SIcon = styled.svg`
  width: 16px;
  height: 20px;
  flex-shrink: 0;
`;

const SRewardIcon = styled.svg`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const SValue = styled.span<{ $tone: tTone; $bar: boolean }>`
  font-size: ${({ $bar }) => ($bar ? 'clamp(0.875rem, 4vw, 1.125rem)' : '1.375rem')};
  font-weight: 700;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => TONE_COLOR[$tone]};
`;

/* Inline unit in the bar variant. Hidden from assistive tech: the card's
   aria-label already spells the three stats out. */
const SUnit = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--mp-text-muted);
  white-space: nowrap;
`;

const SLabel = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 0.75rem;
  color: var(--mp-text-muted);
  white-space: nowrap;
`;
